// src/components/IdleTimer.js
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthProvider from '../providers/AuthProvider';
import AuthService from '../services/AuthService';

const SURVEY_EXEMPT_ROUTES = [
    '/login',
    '/forgot-password',
    '/restore-password',
    '/shopify-auth-landing',
    '/register',
];

function checkRouteDoesntContainExemptKeyword(pathname) {
    return !SURVEY_EXEMPT_ROUTES.some(exemptRoute => pathname.includes(exemptRoute));
}

const IdleTimer = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, setUser, checkPermission } = AuthProvider.useAuth();
    const isAdmin = checkPermission('role-create');

    useEffect(() => {
        if (!user || isAdmin || !checkRouteDoesntContainExemptKeyword(location.pathname)) {
            return;
        }
        // 2 hours in ms, default if env not set
        const idleTimeout = process.env.REACT_APP_IDLE_TIMEOUT ?
            parseInt(process.env.REACT_APP_IDLE_TIMEOUT) : 7200000;

        let idleTimer = null;

        // Reset the timer on user activity
        const resetTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => handleTimeout(), idleTimeout);
        };

        // Handle timeout - log the user out
        const handleTimeout = async () => {
            if (checkRouteDoesntContainExemptKeyword(location.pathname)) {
                try {
                    // Call logout API
                    await AuthService.logout();
                    // Update auth context
                    setUser(null);
                    // Redirect to login
                    navigate('/login', { state: { message: 'You have been logged out due to inactivity.' } });
                } catch (error) {
                    console.error('Logout failed:', error);
                    navigate('/login');
                }
            }
        };

        // Add event listeners for user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        events.forEach(event => {
            document.addEventListener(event, resetTimer);
        });

        // Initialize timer
        resetTimer();

        // Cleanup
        return () => {
            clearTimeout(idleTimer);
            events.forEach(event => {
                document.removeEventListener(event, resetTimer);
            });
        };
    }, [navigate, setUser, location.pathname, isAdmin, user]);

    return null;
};

export default IdleTimer;